import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Button from "../Common/Button/Button";
import DataTable from "../Common/DataTable/DataTable";
import IncrementalPagination from "../Common/DataTable/IncrementalPagination";
import ScreenLoader from "../Common/ScreenLoader/ScreenLoader";
import AppSnackbar from "../Common/Snackbar/AppSnackbar";
import AppRadioGroup from "../Common/FormInputs/AppRadioGroup";
import { useDesignTokens } from "../../theme/useDesignTokens";
import customConfig from "../../config";
import {
  fetchAllInAppNotifications,
  getInAppNotificationErrorMessage,
  getInAppNotifications,
  parseInAppNotificationList,
} from "../../action/inAppNotification";
import {
  isValidInAppNotificationListItem,
  matchesTargetType,
  parseExpiryValue,
  sumTargetUserCounts,
} from "../../utils/inAppNotificationUtils";
import {
  buildNotificationListRouteState,
  buildNotificationListState,
  DEFAULT_NOTIFICATION_LIST_TAB_UI,
  getInAppNotificationPermission,
  IN_APP_NOTIFICATION_ROUTES,
  inAppListTableShellSx,
  inAppPrimaryActionSx,
  inAppRadioGroupResponsiveSx,
  inAppToolbarRowSx,
  resolveNotificationListStateFromLocation,
  setPendingListPagination,
  TARGET_TYPE_OPTIONS,
} from "../../constants/inAppNotificationConstant";
import { createInAppNotificationColumns } from "./inAppNotificationColumns";
import DeleteNotificationDialog from "./DeleteNotificationDialog";
import NotificationTargetUserSummary from "./NotificationTargetUserSummary";

const sortNotifications = (items, orderBy, order) => {
  if (!orderBy) return items;

  return [...items].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (orderBy === "expiry") {
      aValue = parseExpiryValue(a.expiry)?.getTime() ?? 0;
      bValue = parseExpiryValue(b.expiry)?.getTime() ?? 0;
    }

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return order === "asc" ? 1 : -1;
    if (bValue == null) return order === "asc" ? -1 : 1;

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });
};

const NotificationsTab = ({ userRoles = [], userLevel = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isCompactTable = useMediaQuery(theme.breakpoints.down("sm"));
  const { colors } = useDesignTokens();

  const initialTabStateRef = useRef(null);
  if (initialTabStateRef.current === null) {
    initialTabStateRef.current = {
      ...DEFAULT_NOTIFICATION_LIST_TAB_UI,
      ...(resolveNotificationListStateFromLocation(location) ?? {}),
    };
  }
  const initialTabState = initialTabStateRef.current;

  const [targetType, setTargetType] = useState(initialTabState.targetType);
  const [allItems, setAllItems] = useState([]);
  const [page, setPage] = useState(initialTabState.page);
  const [rowsPerPage, setRowsPerPage] = useState(initialTabState.rowsPerPage);
  const [orderBy, setOrderBy] = useState(initialTabState.orderBy);
  const [order, setOrder] = useState(initialTabState.order);
  const [loading, setLoading] = useState(false);
  const [msgToDisplay, setMsgToDisplay] = useState(customConfig.noDataMsg);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, data: null });
  const [apiFeedbackMsg, setApiFeedbackMsg] = useState({
    open: false,
    type: "info",
    msg: "",
  });

  const canCreate = getInAppNotificationPermission(
    userRoles,
    targetType === "broadcast" ? "createBroadcast" : "createUserSpecific",
    userLevel,
  );
  const canEdit = getInAppNotificationPermission(userRoles, "editNotification", userLevel);
  const canDelete = getInAppNotificationPermission(userRoles, "deleteNotification", userLevel);

  const buildListRouteState = useCallback(
    (extra = {}) =>
      buildNotificationListRouteState({
        targetType,
        listState: buildNotificationListState({
          page,
          rowsPerPage,
          orderBy,
          order,
          targetType,
        }),
        ...extra,
      }),
    [page, rowsPerPage, orderBy, order, targetType],
  );

  useEffect(() => {
    const navigationState = location.state || {};

    if (navigationState.notificationTargetType && !navigationState.restoreListPagination) {
      setTargetType(navigationState.notificationTargetType);
    }

    if (navigationState.refresh) {
      setPage(0);
      setRefreshKey((prev) => prev + 1);
    }

    if (navigationState.feedback?.msg) {
      setApiFeedbackMsg({
        open: true,
        type: navigationState.feedback.type || "success",
        msg: navigationState.feedback.msg,
      });
      navigate(location.pathname, {
        replace: true,
        state: buildNotificationListRouteState({
          targetType:
            navigationState.notificationTargetType ||
            navigationState.listState?.targetType,
        }),
      });
    }
  }, [location.pathname, location.state, navigate]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setMsgToDisplay(customConfig.FETCHING_DATA);

    try {
      let items = await fetchAllInAppNotifications({ includeExpired: true });

      if (items.length === 0) {
        const response = await getInAppNotifications({
          page: 0,
          size: 10,
          includeExpired: true,
        });
        const parsed = parseInAppNotificationList(response?.data, 10);
        items = parsed.items.filter((item) => isValidInAppNotificationListItem(item));
      }

      setAllItems(items);
      setMsgToDisplay(items.length ? customConfig.FETCH_SUCCESS : customConfig.noDataMsg);
    } catch (error) {
      const errorMsg = getInAppNotificationErrorMessage(error, customConfig.FETCH_FAILURE);
      setMsgToDisplay(errorMsg);
      setAllItems([]);
      setApiFeedbackMsg({ open: true, type: "error", msg: errorMsg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, refreshKey]);

  const filteredItems = useMemo(
    () => allItems.filter((item) => matchesTargetType(item.targetType, targetType)),
    [allItems, targetType],
  );

  const totalTargetUserCount = useMemo(
    () => (targetType === "user-specific" ? sumTargetUserCounts(filteredItems) : 0),
    [filteredItems, targetType],
  );

  const sortedItems = useMemo(
    () => sortNotifications(filteredItems, orderBy, order),
    [filteredItems, orderBy, order],
  );

  const maxPage = Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1);

  useEffect(() => {
    if (loading || sortedItems.length === 0) {
      return;
    }
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [loading, maxPage, page, sortedItems.length]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedItems.slice(start, start + rowsPerPage);
  }, [sortedItems, page, rowsPerPage]);

  const isLastPage = page >= maxPage;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const navigateToView = useCallback(
    (row) => {
      const listState = buildNotificationListState({
        page,
        rowsPerPage,
        orderBy,
        order,
        targetType,
      });

      setPendingListPagination(listState);
      navigate(IN_APP_NOTIFICATION_ROUTES.view(row.id), {
        state: buildNotificationListRouteState({
          targetType,
          listState,
          targetUserCount: row.targetUserCount,
        }),
      });
    },
    [navigate, order, orderBy, page, rowsPerPage, targetType],
  );

  const columns = useMemo(
    () =>
      createInAppNotificationColumns(
        {
          onEdit: (row) =>
            navigate(IN_APP_NOTIFICATION_ROUTES.edit(row.id), {
              state: buildListRouteState({ targetUserCount: row.targetUserCount }),
            }),
          onView: navigateToView,
          onDelete: (row) => setDeleteDialog({ open: true, data: row }),
          canEdit,
          canDelete,
        },
        { compact: isCompactTable },
      ),
    [buildListRouteState, canDelete, canEdit, isCompactTable, navigate, navigateToView],
  );

  const tableMessage =
    sortedItems.length === 0 && allItems.length > 0
      ? `No ${targetType === "user-specific" ? "user-specific" : "broadcast"} notifications found`
      : msgToDisplay;

  if (loading) {
    return <ScreenLoader message={msgToDisplay} />;
  }

  return (
    <>
      <Box sx={{ py: 1, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <Box
          sx={{
            mb: 1.5,
            pb: 1.5,
            borderBottom: `1px solid ${colors?.ghost?.light}`,
          }}
        >
          <Box sx={inAppToolbarRowSx}>
            <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Target Type
              </Typography>
              <AppRadioGroup
                row
                name="notification-target-type"
                value={targetType}
                onChange={(e) => {
                  const nextTargetType = e.target.value;
                  setTargetType(nextTargetType);
                  setPage(0);

                  if (location.state?.notificationTargetType) {
                    navigate(location.pathname, {
                      replace: true,
                      state: buildNotificationListRouteState({
                        listState: buildNotificationListState({
                          page: 0,
                          rowsPerPage,
                          orderBy,
                          order,
                          targetType: nextTargetType,
                        }),
                      }),
                    });
                  }
                }}
                options={TARGET_TYPE_OPTIONS}
                sx={inAppRadioGroupResponsiveSx}
              />
            </Box>
            {canCreate && (
              <Button
                variant="primary"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(
                    `${IN_APP_NOTIFICATION_ROUTES.create}?targetType=${encodeURIComponent(targetType)}`,
                    { state: buildListRouteState() },
                  )
                }
                sx={inAppPrimaryActionSx}
              >
                Create Notification
              </Button>
            )}
          </Box>

          {targetType === "user-specific" && (
            <NotificationTargetUserSummary
              targetUserCount={totalTargetUserCount}
              label="Total targeted users:"
            />
          )}
        </Box>

        <Box sx={inAppListTableShellSx}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: "100%",
              backgroundColor: "#F5F5F5",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              zIndex: 0,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              flex: 1,
              position: "relative",
              zIndex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <DataTable
              columns={columns}
              data={paginatedItems}
              orderBy={orderBy}
              order={order}
              onSort={handleRequestSort}
              rowKey="id"
              striped
              hoverEffect
              maxHeight="100%"
              sx={{ height: "100%" }}
              tableContainerSx={{ height: "100%", maxHeight: "100%" }}
              msgForDisplay={tableMessage}
            />
          </Box>

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              backgroundColor: "white",
              borderTop: "1px solid #F5F5F5",
              flexShrink: 0,
            }}
          >
            <IncrementalPagination
              page={page}
              pageSize={rowsPerPage}
              dataLength={paginatedItems.length}
              isLastPage={isLastPage}
              pageSizeOptions={[10, 20, 50]}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(size) => {
                setPage(0);
                setRowsPerPage(size);
              }}
            />
          </Box>
        </Box>
      </Box>

      {deleteDialog.open && (
        <DeleteNotificationDialog
          open={deleteDialog.open}
          notification={deleteDialog.data}
          onClose={(result) => {
            setDeleteDialog({ open: false, data: null });
            if (result?.success) {
              setPage(0);
              setRefreshKey((prev) => prev + 1);
            }
          }}
          feedbackHandler={(feedback) =>
            setApiFeedbackMsg({ ...feedback, open: true })
          }
        />
      )}

      <AppSnackbar
        open={apiFeedbackMsg.open}
        type={apiFeedbackMsg.type}
        message={apiFeedbackMsg.msg}
        onClose={() => setApiFeedbackMsg((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default NotificationsTab;
